/**
 * Camera control. Everything reported here is read back from the live track;
 * nothing is assumed from a constraint we asked for.
 */

export type Facing = 'user' | 'environment';

export interface CameraSettings {
  facing: Facing;
  /** Requested height. 720 by default; 1080 only when the device allows it. */
  height: 720 | 1080;
  frameRate: 30 | 24 | 15;
}

export interface CameraReadout {
  active: boolean;
  facing: Facing | null;
  width: number | null;
  height: number | null;
  frameRate: number | null;
  supports: { zoom: boolean; torch: boolean; focus: boolean };
}

/** Extra capabilities browsers expose but the DOM lib does not type. */
interface ExtendedCapabilities extends MediaTrackCapabilities {
  zoom?: { min: number; max: number; step: number };
  torch?: boolean;
  focusMode?: string[];
  facingMode?: string[];
}

interface ExtendedConstraints extends MediaTrackConstraintSet {
  zoom?: number;
  torch?: boolean;
}

export const DEFAULT_SETTINGS: CameraSettings = {
  facing: 'environment',
  /* The spec's practical target: 720p30. Never 60 by default — the target PC has
     no discrete GPU. */
  height: 720,
  frameRate: 30,
};

export class Camera {
  private stream: MediaStream | null = null;
  private settings: CameraSettings = { ...DEFAULT_SETTINGS };

  get track(): MediaStreamTrack | null {
    return this.stream?.getVideoTracks()[0] ?? null;
  }

  get mediaStream(): MediaStream | null {
    return this.stream;
  }

  get current(): CameraSettings {
    return { ...this.settings };
  }

  get isActive(): boolean {
    return this.track?.readyState === 'live';
  }

  /**
   * Starts, or restarts with new settings. `ideal` rather than `exact` so a
   * device that cannot do 1080p downgrades instead of throwing.
   */
  async start(partial: Partial<CameraSettings> = {}): Promise<void> {
    this.settings = { ...this.settings, ...partial };
    const { facing, height, frameRate } = this.settings;

    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error("Ce navigateur n'expose pas getUserMedia. Utilisez HTTPS et un navigateur récent.");
    }

    const next = await navigator.mediaDevices.getUserMedia({
      audio: false,
      video: {
        facingMode: { ideal: facing },
        width: { ideal: Math.round((height * 16) / 9) },
        height: { ideal: height },
        frameRate: { ideal: frameRate, max: frameRate },
      },
    });

    /* Swap after the new stream exists, so a failed restart leaves the old one
       running rather than killing the camera. Tracks the new stream reuses are
       spared: a browser may hand back the same track when the constraints match
       what is already open, and stopping it would kill the live camera. */
    const keep = new Set(next.getTracks());
    this.stream?.getTracks().forEach((t) => {
      if (!keep.has(t)) t.stop();
    });
    this.stream = next;
  }

  stop(): void {
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;
  }

  /** The real state of the track, for the PC and for the on-screen read-out. */
  readout(): CameraReadout {
    const track = this.track;
    if (!track || track.readyState !== 'live') {
      return {
        active: false,
        facing: null,
        width: null,
        height: null,
        frameRate: null,
        supports: { zoom: false, torch: false, focus: false },
      };
    }

    const s = track.getSettings();
    const caps: ExtendedCapabilities = track.getCapabilities?.() ?? {};
    const facing = s.facingMode === 'user' || s.facingMode === 'environment' ? s.facingMode : null;

    return {
      active: true,
      facing,
      width: s.width ?? null,
      height: s.height ?? null,
      /* Rounded because browsers report fractional rates like 29.97. */
      frameRate: s.frameRate ? Math.round(s.frameRate) : null,
      supports: {
        zoom: caps.zoom !== undefined,
        torch: caps.torch === true,
        focus: Array.isArray(caps.focusMode) && caps.focusMode.length > 0,
      },
    };
  }

  /** Zoom range as the device reports it, or null when unsupported. */
  zoomRange(): { min: number; max: number; step: number } | null {
    const caps: ExtendedCapabilities = this.track?.getCapabilities?.() ?? {};
    return caps.zoom ?? null;
  }

  async setZoom(value: number): Promise<void> {
    const track = this.track;
    if (!track || !this.zoomRange()) return;
    await track.applyConstraints({ advanced: [{ zoom: value } as ExtendedConstraints] });
  }

  /**
   * Applies the torch constraint. Resolves `false` — not an error — when the
   * track cannot carry torch at all, so the caller never prints a state the
   * device did not confirm.
   */
  async setTorch(on: boolean): Promise<boolean> {
    const track = this.track;
    const caps: ExtendedCapabilities = track?.getCapabilities?.() ?? {};
    if (!track || caps.torch !== true) return false;
    await track.applyConstraints({ advanced: [{ torch: on } as ExtendedConstraints] });
    return true;
  }

  /** Real capabilities from getCapabilities(), never a guess. */
  getCapabilitiesReport(): { torch: boolean; zoom: { min: number; max: number; step: number } | null; lenses: string[] } | null {
    const track = this.track;
    if (!track || track.readyState !== 'live') return null;
    const caps: ExtendedCapabilities = track.getCapabilities?.() ?? {};
    const torch = caps.torch === true;
    const zoom = caps.zoom ?? null;
    // Lenses from facingMode capability when the browser exposes it — otherwise fall back
    // to the actually running facingMode so we never claim a lens we did not measure.
    let lenses: string[] = [];
    if (Array.isArray(caps.facingMode) && caps.facingMode.length > 0) {
      lenses = caps.facingMode.map((f) => (f === 'user' ? 'front' : f === 'environment' ? 'back' : f));
    } else {
      const facing = track.getSettings().facingMode;
      if (facing === 'user') lenses = ['front'];
      else if (facing === 'environment') lenses = ['back'];
      else if (this.isActive) lenses = [this.settings.facing === 'user' ? 'front' : 'back'];
    }
    lenses = [...new Set(lenses)];
    return { torch, zoom, lenses };
  }

  /** Whether a second camera exists at all, so the flip button can be honest. */
  static async hasMultipleCameras(): Promise<boolean> {
    if (!navigator.mediaDevices?.enumerateDevices) return false;
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      return devices.filter((d) => d.kind === 'videoinput').length > 1;
    } catch {
      return false;
    }
  }
}
